import { useRef, useState, type Dispatch, type SetStateAction } from 'react'
import {
  descontoInvalido, MSG_DESCONTO, MSG_CONDICAO_VAZIA, CONDICAO_EM_EDICAO_VAZIA,
  type CondicaoRascunho, type CondicaoEmEdicao, type ErrosDaPolitica,
} from '../../representadas/rascunhoDaPolitica'
import { condicaoPorExtenso, ordenarPorPrazo } from '../../representadas/condicaoPorExtenso'
import { IconeCalendario, IconeTresPontos } from './IconesDaPolitica'
import { ESTILO_CAMPO, ESTILO_CARTAO_NOVO, ESTILO_ICONE_CARTAO, ESTILO_SELO_DESCONTO } from './estilosDaFornecedora'

// Spec 27/09 (1.4): card de altura fixa, lista rolando por dentro, desconto
// como SELO. Continua tudo em RASCUNHO (revisao de 24/09): adicionar,
// editar e excluir so valem no Salvar da aba. O "adicionar" continua sempre
// visivel na edicao -- o [+] so leva o foco ate ele (Task 16 levantou o
// texto para FormRepresentada, que avisa se ficou digitado e esquecido).
export default function CondicoesPagamento({ itens, setItens, edicao, setEdicao, erros, editavel }: {
  itens: CondicaoRascunho[]
  setItens: Dispatch<SetStateAction<CondicaoRascunho[]>>
  edicao: CondicaoEmEdicao
  setEdicao: Dispatch<SetStateAction<CondicaoEmEdicao>>
  erros: ErrosDaPolitica
  editavel: boolean
}) {
  const { descricao, desconto, editandoChave, erroForm } = edicao
  const visiveis = ordenarPorPrazo(itens.filter((c) => !c.removida), (c) => c.descricao)
  const [menuAberto, setMenuAberto] = useState<string | null>(null)
  const campo = useRef<HTMLInputElement>(null)

  const limpar = () => setEdicao(CONDICAO_EM_EDICAO_VAZIA)

  function aplicar() {
    if (descricao.trim() === '') { setEdicao((e) => ({ ...e, erroForm: MSG_CONDICAO_VAZIA })); return }
    if (descontoInvalido(desconto)) { setEdicao((e) => ({ ...e, erroForm: MSG_DESCONTO })); return }
    if (editandoChave !== null) {
      setItens((l) => l.map((c) => (c.chave === editandoChave ? { ...c, descricao: descricao.trim(), desconto: desconto.trim() } : c)))
    } else {
      setItens((l) => [...l, { chave: `nova-cond-${Date.now()}-${l.length}`, id: null, descricao: descricao.trim(), desconto: desconto.trim(), removida: false }])
    }
    limpar()
  }

  function editar(c: CondicaoRascunho) {
    setMenuAberto(null)
    setEdicao({ descricao: c.descricao, desconto: c.desconto, editandoChave: c.chave, erroForm: null })
    campo.current?.focus()
  }

  function excluir(c: CondicaoRascunho) {
    setMenuAberto(null)
    setItens((l) => (c.id === null ? l.filter((x) => x.chave !== c.chave) : l.map((x) => (x.chave === c.chave ? { ...x, removida: true } : x))))
    if (editandoChave === c.chave) limpar()
  }

  // Enter no campo ADICIONA -- nunca envia o formulario da aba.
  const enterAplica = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') { e.preventDefault(); aplicar() }
  }

  return (
    <div className={`${ESTILO_CARTAO_NOVO} h-[21rem] flex flex-col min-w-0`} data-testid="secao-condicoes-pagamento">
      <div className="flex items-start gap-2">
        <span className={ESTILO_ICONE_CARTAO}><IconeCalendario /></span>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-slate-800 leading-tight">Condições de pagamento</h3>
        </div>
      </div>
      {/* Rola por dentro (pedido dela, 27/09). SEM overscroll-contain: no fim
          da lista o dedo tem de passar a rolar a pagina. */}
      <ul className="mt-2 flex-1 overflow-y-auto space-y-1.5 pr-0.5">
        {visiveis.length === 0 && <li className="text-xs text-slate-500">Nenhuma condição cadastrada.</li>}
        {visiveis.map((c) => {
          const extenso = condicaoPorExtenso(c.descricao)
          return (
            <li key={c.chave} className="relative rounded-xl border border-slate-200 px-2 py-1.5">
              <div className="flex items-start gap-1.5">
                <span className="mt-0.5 text-slate-500"><IconeCalendario tamanho={16} /></span>
                <div className="min-w-0 flex-1">
                  {/* Pedido dela (28/09): o por extenso E o titulo; o "7/14/21"
                      digitado nao aparece. Sem extenso ("A vista"), o digitado. */}
                  <p className="text-sm font-medium text-slate-800 break-words">{extenso ?? c.descricao}</p>
                  <div className="flex flex-wrap items-center gap-1">
                    {c.desconto !== '' && <span className={ESTILO_SELO_DESCONTO}>{c.desconto}% desc.</span>}
                  </div>
                </div>
                {editavel && (
                  <button type="button" aria-label={`Ações de ${c.descricao}`} aria-expanded={menuAberto === c.chave}
                    onClick={() => setMenuAberto((m) => (m === c.chave ? null : c.chave))}
                    className="shrink-0 text-slate-500 px-0.5"><IconeTresPontos /></button>
                )}
              </div>
              {menuAberto === c.chave && (
                <div className="mt-1 flex gap-2 justify-end">
                  <button type="button" onClick={() => editar(c)} className="text-xs text-teal-800 underline">Editar</button>
                  <button type="button" onClick={() => excluir(c)} className="text-xs text-red-700 underline">Excluir</button>
                </div>
              )}
              {erros[c.chave] && <p className="text-xs text-red-700">{erros[c.chave]}</p>}
            </li>
          )
        })}
      </ul>
      {editavel && (
        <div className="mt-2 space-y-1">
          <input ref={campo} value={descricao} onChange={(e) => setEdicao((ed) => ({ ...ed, descricao: e.target.value, erroForm: null }))}
            onKeyDown={enterAplica} placeholder="Ex.: 30/60/90" className={`${ESTILO_CAMPO} text-sm py-1.5`} />
          <div className="flex gap-1">
            <input aria-label="Desconto para esta condição (%), opcional" value={desconto}
              onChange={(e) => setEdicao((ed) => ({ ...ed, desconto: e.target.value, erroForm: null }))} onKeyDown={enterAplica}
              placeholder="% desc." inputMode="decimal" className={`${ESTILO_CAMPO} text-sm py-1.5 min-w-0`} />
            <button type="button" onClick={aplicar} className="shrink-0 bg-teal-700 text-white rounded-lg px-2 text-xs">
              {editandoChave !== null ? 'Atualizar' : 'Adicionar'}
            </button>
          </div>
          {editandoChave !== null && (
            <button type="button" onClick={limpar} className="text-slate-600 text-xs underline">Cancelar</button>
          )}
          {erroForm && <p className="text-xs text-red-600">{erroForm}</p>}
        </div>
      )}
    </div>
  )
}
